import * as React from 'react';
import { useIntl } from 'react-intl';
import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { always } from 'ramda';

import { Layout } from '@components/layout';
import api from '@services/api';
import reportError from '@utils/reportError';
import { getPreviewToken, redirectOnError } from '@utils/helpers';
import { ArticleListItem } from '@definitions/articles.types';
import { EditorialList } from '@components/editorialList';
import { renderWhenTrueOtherwise } from '@utils/rendering';
import { HeroCarousel } from '@components/heroCarousel';
import { Homepage as PageType } from '@definitions/homepage.types';
import { Separator } from '@components/editorialFilter/editorialFilter.styles';
import { FILTER_TYPE } from '@components/editorialList/editorialList.constants';

export const getServerSideProps = async ({ req }: GetServerSidePropsContext) => {
  try {
    const token = getPreviewToken(req.headers.cookie);

    const { data } = await api.get('/features/', {
      headers: { cookie: req.headers.cookie || '', authorization: token },
    });

    if (!data) {
      return { notFound: true };
    }

    return {
      props: { articles: data.items, pageData: { header: data.header } },
    };
  } catch (e) {
    reportError(e);

    return redirectOnError(e);
  }
};

interface SlugProps {
  articles: ArticleListItem[];
  pageData: PageType;
}

const Slug = ({ articles, pageData }: SlugProps) => {
  const intl = useIntl();
  const headerTitle = intl.formatMessage({
    id: 'generalPage.headerTitle',
    defaultMessage: 'Platform Features | Discover New Artists',
  });
  const headerDescription = intl.formatMessage({
    id: 'generalPage.headerDescription',
    defaultMessage:
      'Discover the latest editorial features on Platform, including artist interviews, studio visits, city guides, guest edits and more.',
  });
  const renderHeroCarousel = renderWhenTrueOtherwise(
    always(<HeroCarousel slides={pageData.header} />),
    always(<Separator />)
  );

  const { query } = useRouter();

  return (
    <Layout title={headerTitle} defaultDescription={headerDescription}>
      {renderHeroCarousel(!!pageData.header?.length && (query.filter === FILTER_TYPE.ALL || !query.filter))}
      <EditorialList articles={articles} />
    </Layout>
  );
};

export default Slug;
